import IconFont from "@/components/IconFont";
import Popup from "@/components/popup";
import SafeArea from "@/components/safe-area";
import { SelectView } from "@/components/search-select/select-view";
import { useCountryCode } from "@/hooks/useRequest/useCountryCode";
import { RegionModel } from "@/interfaces/loginTypes";
import { useRouter } from "next/router";
import { useState, useEffect, useMemo } from "react";
import styles from "@/components/CountryCodeSelect/index.module.scss";

import { useAppSetting } from "@/service/common.services";
import Input from "@/components/Input";

interface Props {
	value?: string;
	visible: boolean;
	onChange: (val: string) => void;
	initialValue?: string;
}

const CN_PHONE_CODE = "0086";
const timeStream = Date.now();
export default function CountryCodeSelect({ visible, onChange, value, initialValue }: Props) {
	const router = useRouter();
	const { data = [] } = useCountryCode(router.locale || "en");
	const { data: configs } = useAppSetting(timeStream);

	const [searchText, setSearchText] = useState("");
	const [width, setWidth] = useState(0);
	const [open, setOpen] = useState(false);

	const initialPhoneCode = useMemo(() => {
		if (!configs || !data || !Array.isArray(configs)) return CN_PHONE_CODE;
		const region = configs.find((item) => item.key === "country").value;
		if (!region) return CN_PHONE_CODE;

		const _phoneCode = data.find((item) => item.acronym === region)?.phoneCode;
		if (!_phoneCode) return CN_PHONE_CODE;

		const phoneCode = _phoneCode ? _phoneCode.padStart(4, "0") : CN_PHONE_CODE;
		return phoneCode;
	}, [configs, data]);

	const filterOptions = (filterText: string, defaultOptions: RegionModel[]) => {
		if (!filterText) return defaultOptions.map(transformCountryOptions);
		return data.filter((item) => filterWithText(filterText, item)).map(transformCountryOptions);
	};
	const filterWithText = (text: string, item: RegionModel) => {
		return (
			item.phoneCode.includes(text) ||
			item.acronym.toLocaleLowerCase().includes(text.toLocaleLowerCase()) ||
			item.name.toLocaleLowerCase().includes(text.toLocaleLowerCase())
		);
	};
	const transformCountryOptions = (item: RegionModel) => {
		return {
			label: `${item.name} +${item.phoneCode}`,
			acronym: item.acronym,
			value: item.phoneCode,
		};
	};

	const countryCodeText = () => {
		const selectCountry = data.find((item) => parseInt(item.phoneCode) === parseInt(value || ""));
		if (!selectCountry) return "";

		return `${selectCountry.acronym} +${
			selectCountry.phoneCode.length >= 4 ? selectCountry.phoneCode.substring(0, 1) : selectCountry.phoneCode
		}`;
	};

	useEffect(() => {
		if (visible) {
			const size = getTextSize(countryCodeText(), "20px");
			setWidth(Math.ceil((size.width || 20) + 64));
			if (!value) {
				// 初始化

				onChange(value || CN_PHONE_CODE);
			}
		} else {
			setWidth(0);
		}
	}, [visible]);

	useEffect(() => {
		onChange(initialPhoneCode);
	}, [initialPhoneCode]);

	const getTextSize = (txt: string, font: string) => {
		const element = document.createElement("canvas");
		const context = element.getContext("2d") as any;
		context.font = font;
		const tSize = {
			width: context.measureText(txt).width,
			height: parseInt(context.font),
		};
		return tSize;
	};

	const placeHolder = (
		<div
			className="flex flex-shrink-0 items-center justify-center overflow-hidden whitespace-nowrap"
			style={{
				transitionDuration: "0.25s",
				transitionTimingFunction: "ease-in",
				width: width,
			}}
			onClick={() => {
				setOpen(true);
			}}
		>
			<a>{countryCodeText()}</a>
			<span className="ml-4 h-5 w-[1px] flex-shrink-0 bg-light"></span>
		</div>
	);

	return (
		<>
			{placeHolder}
			<Popup
				visible={open}
				onMaskClick={() => setOpen(false)}
				destroyOnClose
				bodyStyle={{
					borderRadius: "12px 12px 0 0",
				}}
			>
				<div className={styles.container}>
					<Input
						name="searchText"
						value={searchText}
						onChange={(e) => setSearchText(e)}
						onClear={() => setSearchText("")}
						size="lg"
						aria-label="search"
						placeholder="search"
						id="search_text"
						leading={
							<IconFont
								type="search-0"
								className=" text-medium"
								style={{
									fontSize: "1.5rem",
								}}
							/>
						}
					/>

					<SelectView
						className={styles.scrollView}
						list={filterOptions(searchText, data)}
						onSelected={(e) => {
							onChange(e.value.padStart(4, "0"));
							setOpen(false);
						}}
					/>
					<SafeArea position="bottom" />
				</div>
			</Popup>
		</>
	);
}
